import {
  Suspense,
  lazy,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import type { CustomRendererProps } from 'streamdown'
import { Download, Maximize2, type LucideIcon } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CodeBlock } from '@/components/ai/code-block'
import type { VegaView } from './vega-chart'

// React.lazy: vega ships as its own chunk, loaded on the first chart.
const VegaChart = lazy(() => import('./vega-chart'))

// Hard caps on untrusted model output before it reaches vegaEmbed. The prompt
// asks David for ~100 rows; these are the safety ceiling, not the target.
const MAX_CODE_LENGTH = 100_000
const MAX_DATA_ROWS = 500

// Matches Streamdown's table action buttons exactly (14px ghost icons).
const ACTION_BTN =
  'cursor-pointer p-1 text-muted-foreground transition-[color,background-color,border-color,box-shadow,opacity] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50'

const NOOP = () => {}

type Spec = Record<string, unknown> & {
  title?: unknown
  data?: { values?: unknown }
}

function parseSpec(code: string): Spec | null {
  // Gate on raw length BEFORE parsing so a huge blob can't allocate/parse
  // before the cap applies.
  if (code.length > MAX_CODE_LENGTH) return null
  try {
    const parsed = JSON.parse(code)
    return parsed && typeof parsed === 'object' ? (parsed as Spec) : null
  } catch {
    return null
  }
}

function inlineValues(spec: Spec): Record<string, unknown>[] | null {
  const values = spec.data?.values
  return Array.isArray(values) ? (values as Record<string, unknown>[]) : null
}

// Vega-Lite keys that hold nested sub-specs (layer/facet/etc.).
const SUBSPEC_KEYS = ['spec', 'layer', 'concat', 'hconcat', 'vconcat', 'facet']

// Validate an untrusted (prompt-injectable) LLM spec before it reaches vega:
// every data source must be an inline `values` array — reject external fetch
// (`url`), data generators (`sequence`/`graticule`), named-dataset refs, and
// image marks. Also sums rows across ALL inline arrays for the cap, so a
// generator or a layered spec can't slip past a top-level-only row count.
// Walks spec structure only (never descends into data rows/encoding), so a
// datum FIELD named "url" is not mistaken for a `data.url` source.
function analyzeSpec(node: unknown): { safe: boolean; rows: number } {
  let safe = true
  let rows = 0
  const visit = (n: unknown): void => {
    if (!safe || !n || typeof n !== 'object') return
    if (Array.isArray(n)) return n.forEach(visit)
    const obj = n as Record<string, unknown>
    const mark = obj.mark
    if (
      mark === 'image' ||
      (mark as { type?: unknown } | undefined)?.type === 'image'
    ) {
      safe = false
      return
    }
    // Reject encoding.href — Vega-Lite compiles it into an <a xlink:href>
    // around each mark, i.e. an attacker-controlled click-through/phishing link.
    const enc = obj.encoding
    if (enc && typeof enc === 'object' && 'href' in enc) {
      safe = false
      return
    }
    if (obj.data && typeof obj.data === 'object') {
      const d = obj.data as Record<string, unknown>
      if ('url' in d || 'sequence' in d || 'graticule' in d) {
        safe = false
        return
      }
      if (Array.isArray(d.values)) rows += d.values.length
      else if ('values' in d) {
        safe = false // non-array inline data (CSV/JSON string + format) — uncapped by row count
        return
      } else if ('name' in d) {
        safe = false // named-dataset reference — off-convention bypass
        return
      }
    }
    if (obj.datasets && typeof obj.datasets === 'object') {
      for (const v of Object.values(obj.datasets as Record<string, unknown>)) {
        if (!Array.isArray(v)) {
          safe = false
          return
        }
        rows += v.length
      }
    }
    for (const key of SUBSPEC_KEYS) if (key in obj) visit(obj[key])
  }
  visit(node)
  return { safe, rows }
}

function specTitle(spec: Spec): string {
  const t = spec.title
  if (typeof t === 'string') return t
  if (
    t &&
    typeof t === 'object' &&
    typeof (t as { text?: unknown }).text === 'string'
  ) {
    return (t as { text: string }).text
  }
  return 'Chart'
}

function slugify(title: string): string {
  return (
    title
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'chart'
  )
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: LucideIcon
  label: string
  onClick?: () => void
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className={ACTION_BTN}>
          <Icon className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

// Save-as-PNG + Expand-to-fullscreen, mirroring the table's top-right action
// row. Only rendered for a real chart; ChartCard reserves the strip height in
// the other states so there's no layout shift (and no phantom controls for AT).
function ChartActions({
  title,
  spec,
  onSavePng,
}: {
  title: string
  spec: Spec
  onSavePng: () => void
}) {
  return (
    <>
      <ActionButton icon={Download} label="Save as PNG" onClick={onSavePng} />
      <Dialog>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <button type="button" aria-label="Expand" className={ACTION_BTN}>
                <Maximize2 className="size-3.5" />
              </button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>Expand</TooltipContent>
        </Tooltip>
        <DialogContent className="flex h-[90vh] w-[95vw] max-w-none flex-col gap-3 sm:max-w-none">
          <DialogTitle className="text-sm font-medium">{title}</DialogTitle>
          <div className="min-h-0 flex-1">
            <Suspense fallback={<Skeleton className="h-full w-full" />}>
              <VegaChart spec={spec} onError={NOOP} className="h-full w-full" />
            </Suspense>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Styled like Streamdown's table: a tinted strip (action row) over a solid
// bg-background inner surface.
function ChartCard({
  children,
  actions,
  hidden,
}: {
  children: ReactNode
  actions?: ReactNode
  hidden?: boolean
}) {
  return (
    <figure
      className="my-4 flex w-full max-w-[560px] flex-col gap-2 rounded-lg border border-border bg-white-alpha-4 p-2"
      aria-hidden={hidden || undefined}>
      {/* Strip is always present (min-h reserves the button-row height) so the
          streaming/empty states don't shift when a chart's actions appear —
          and those states render no phantom controls for screen readers. */}
      <div className="flex min-h-7 items-center justify-end gap-1">
        {actions && <TooltipProvider>{actions}</TooltipProvider>}
      </div>
      <div className="rounded-md border border-border bg-background p-3">
        {children}
      </div>
    </figure>
  )
}

// Fixed height so skeleton, chart, and empty state share one footprint.
const ChartSkeleton = () => <Skeleton className="h-60 w-full" />

const ChartEmpty = () => (
  <div className="flex h-60 items-center justify-center text-sm text-muted-foreground">
    No data to chart.
  </div>
)

// Non-technical users shouldn't be confronted with raw JSON, but the spec
// stays one click away for anyone debugging what David emitted.
function ChartFallback({ code }: { code: string }) {
  return (
    <div className="my-4 w-full max-w-[560px]">
      <p className="mb-1 text-sm text-muted-foreground">
        Couldn&apos;t render this chart.
      </p>
      <Collapsible>
        <CollapsibleTrigger className="text-xs text-muted-foreground underline-offset-2 hover:underline">
          Show chart source
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 rounded-lg border border-border bg-muted/50 p-3">
          <CodeBlock code={code} language="json" label="chart source" />
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function cell(value: unknown): string {
  if (value == null) return ''
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

// Screen-reader-only equivalent of the chart: the raw numbers behind it. The
// visible SVG is aria-hidden by the caller so AT reads this instead. Memoized
// because trailing report tokens re-render the renderer, and reconciling this
// (up to 500-row) table each time is the one non-trivial cost here; `rows`
// (stable via the spec useMemo) and `title` make the memo hold.
const ChartDataTable = memo(function ChartDataTable({
  title,
  rows,
}: {
  title: string
  rows: Record<string, unknown>[]
}) {
  const columns = Array.from(new Set(rows.flatMap((r) => Object.keys(r))))
  return (
    <table className="sr-only">
      <caption>{title} (data table)</caption>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c} scope="col">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {columns.map((c) => (
              <td key={c}>{cell(row[c])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
})

/**
 * Streamdown custom renderer for ```vega-lite fences.
 *
 *   isIncomplete ──────────────▶ skeleton (streaming)
 *   parse fails / oversized ────▶ fallback (muted caption + collapsed source)
 *   inline data.values === [] ──▶ empty state
 *   valid ─────────────────────▶ lazy VegaChart + action row (Save PNG / Expand)
 *                                 + sr-only data table. Async embed error →
 *                                 fallback via onError; lazy/sync throw →
 *                                 fallback via ErrorBoundary.
 */
export function VegaLiteRenderer({ code, isIncomplete }: CustomRendererProps) {
  const [failed, setFailed] = useState(false)
  const viewRef = useRef<VegaView | null>(null)
  const onError = useCallback(() => setFailed(true), [])
  const handleReady = useCallback((view: VegaView | null) => {
    viewRef.current = view
  }, [])
  // Parse once per stable code string (never mid-stream — partial JSON just
  // throws). Memoized so the spec's identity is stable across parent
  // re-renders, keeping VegaChart from re-embedding on every streamed token.
  const spec = useMemo(
    () => (isIncomplete ? null : parseSpec(code)),
    [code, isIncomplete]
  )

  if (isIncomplete) {
    return (
      <ChartCard hidden>
        <ChartSkeleton />
      </ChartCard>
    )
  }
  if (!spec) return <ChartFallback code={code} />

  // Reject unsafe specs (external fetch / data generators / image marks) and
  // cap total inline rows. Code-length cap is enforced in parseSpec (pre-parse).
  const { safe, rows } = analyzeSpec(spec)
  if (failed || !safe || rows > MAX_DATA_ROWS)
    return <ChartFallback code={code} />
  const values = inlineValues(spec)
  if (values && values.length === 0) {
    return (
      <ChartCard>
        <ChartEmpty />
      </ChartCard>
    )
  }

  const title = specTitle(spec)
  const savePng = async () => {
    const view = viewRef.current
    if (!view) return
    try {
      const url = await view.toImageURL('png', 2) // 2 = 2x export resolution
      // Not migrated to the shared `triggerDownload` helper: this component's
      // existing test mocks `document.createElement` with a bare
      // `{ href, download, click }` object (no `setAttribute`/`style`/DOM
      // insertion), so switching would mean rewriting that mock too — more
      // than the trivial, no-behavior-change swap done for
      // `CallRecordingDownloadButton` and `SqlResultsTable`.
      const a = document.createElement('a')
      a.href = url
      a.download = `${slugify(title)}.png`
      a.click()
    } catch (err) {
      console.error('chart PNG export failed', err)
    }
  }

  return (
    <ErrorBoundary fallback={() => <ChartFallback code={code} />}>
      <ChartCard
        actions={
          <ChartActions title={title} spec={spec} onSavePng={savePng} />
        }>
        <div aria-hidden={values ? true : undefined}>
          <Suspense fallback={<ChartSkeleton />}>
            <VegaChart spec={spec} onError={onError} onReady={handleReady} />
          </Suspense>
        </div>
        {values && <ChartDataTable title={title} rows={values} />}
      </ChartCard>
    </ErrorBoundary>
  )
}
